import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CalendarIcon,
  TagIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { firestore } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { DeleteConfirmation } from '../../ui/DeleteConfirmation';

const FollowUpTasks = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignedToEmail: '',
    assignedToName: '',
    dueDate: '',
    priority: 'medium',
    sessionId: '',
    status: 'pending',
  });

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch clients
    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedCoachEmail', '==', currentUser.email)
    );

    const unsubscribeClients = onSnapshot(clientsQuery, snapshot => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch sessions
    const sessionsQuery = query(
      collection(firestore, 'sessions'),
      where('coachEmail', '==', currentUser.email),
      orderBy('date', 'desc')
    );

    const unsubscribeSessions = onSnapshot(
      sessionsQuery,
      snapshot => {
        setSessions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      },
      error => {
        const simpleQuery = query(
          collection(firestore, 'sessions'),
          where('coachEmail', '==', currentUser.email)
        );
        onSnapshot(simpleQuery, snapshot => {
          setSessions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
      }
    );

    // Fetch tasks related to sessions
    const tasksQuery = query(
      collection(firestore, 'tasks'),
      where('createdBy', '==', currentUser.email),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      snapshot => {
        const allTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Filter tasks that are related to sessions or assigned to coach's clients
        const sessionRelatedTasks = allTasks.filter(task => {
          const isAssignedToCoachClient = clients.some(
            client => client.email === task.assignedToEmail
          );
          return task.sessionId || isAssignedToCoachClient;
        });
        setTasks(sessionRelatedTasks);
        setLoading(false);
      },
      error => {
        console.error('Failed to fetch tasks:', error);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeClients();
      unsubscribeSessions();
      unsubscribeTasks();
    };
  }, [currentUser?.email, clients.length]);

  const addTask = async () => {
    if (!newTask.title || !newTask.assignedToEmail || !newTask.dueDate) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    try {
      const taskData = {
        ...newTask,
        createdBy: currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(firestore, 'tasks'), taskData);
      toast.success('Follow-up task created successfully!');
      setNewTask({
        title: '',
        description: '',
        assignedToEmail: '',
        assignedToName: '',
        dueDate: '',
        priority: 'medium',
        sessionId: '',
        status: 'pending',
      });
      setShowAddTask(false);
    } catch (error) {
      toast.error('Failed to create task', 'Error');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await updateDoc(doc(firestore, 'tasks', taskId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        completedAt: newStatus === 'completed' ? serverTimestamp() : null,
      });
      toast.success('Task status updated!');
    } catch (error) {
      toast.error('Failed to update task status', 'Error');
    }
  };

  const handleDeleteClick = task => {
    setTaskToDelete(task);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'tasks', taskToDelete.id));
      toast.success('Task deleted successfully!');
      setShowDeleteModal(false);
      setTaskToDelete(null);
    } catch (error) {
      toast.error('Failed to delete task', 'Error');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesFilter =
      filter === 'all' ||
      task.status === filter ||
      (filter === 'overdue' &&
        task.status !== 'completed' &&
        task.dueDate &&
        new Date(task.dueDate) < new Date());

    const matchesSearch =
      !searchTerm ||
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.assignedToName?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/50';
      case 'medium':
        return 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/50';
      case 'low':
        return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/50';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
    }
  };

  const getPriorityBadgeColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-r from-green-500 to-emerald-600 text-white';
      case 'in-progress':
        return 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white';
      case 'pending':
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
      case 'overdue':
        return 'bg-gradient-to-r from-red-600 to-red-700 text-white';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    }
  };

  const getStatusBadgeColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'in-progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'overdue':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    overdue: tasks.filter(
      t => t.status !== 'completed' && t.dueDate && new Date(t.dueDate) < new Date()
    ).length,
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
            <p className="font-medium text-gray-600">Loading follow-up tasks...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white p-4 shadow-xl sm:p-6"
    >
      {/* Header */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
            <CheckCircleIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Follow-up Tasks</h2>
            <p className="text-sm text-gray-500">Manage action items from your sessions</p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddTask(true)}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-orange-600 hover:to-red-700 hover:shadow-xl"
        >
          <PlusIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Add Task</span>
        </motion.button>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-4"
        >
          <div className="text-2xl font-bold text-blue-700">{taskStats.total}</div>
          <div className="mt-1 text-xs font-medium text-blue-600">Total Tasks</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-4"
        >
          <div className="text-2xl font-bold text-green-700">{taskStats.completed}</div>
          <div className="mt-1 text-xs font-medium text-green-600">Completed</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100 p-4"
        >
          <div className="text-2xl font-bold text-amber-700">{taskStats.pending}</div>
          <div className="mt-1 text-xs font-medium text-amber-600">Pending</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-cyan-100 p-4"
        >
          <div className="text-2xl font-bold text-cyan-700">{taskStats.inProgress}</div>
          <div className="mt-1 text-xs font-medium text-cyan-600">In Progress</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-red-100 p-4"
        >
          <div className="text-2xl font-bold text-red-700">{taskStats.overdue}</div>
          <div className="mt-1 text-xs font-medium text-red-600">Overdue</div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 pl-10 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
          />
          <TagIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 py-16 text-center"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-200">
              <CheckCircleIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-700">
              {searchTerm || filter !== 'all'
                ? 'No tasks match your filters'
                : 'No follow-up tasks yet'}
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              {searchTerm || filter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first task to get started!'}
            </p>
            {!searchTerm && filter === 'all' && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddTask(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl"
              >
                <PlusIcon className="h-5 w-5" />
                Create First Task
              </motion.button>
            )}
          </motion.div>
        ) : (
          filteredTasks.map((task, index) => {
            const isOverdue =
              task.status !== 'completed' && task.dueDate && new Date(task.dueDate) < new Date();
            const displayStatus = isOverdue ? 'overdue' : task.status;
            const daysUntilDue = task.dueDate
              ? Math.ceil((new Date(task.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
              : null;

            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`group relative overflow-hidden rounded-2xl border-2 transition-all hover:shadow-xl ${
                  displayStatus === 'completed'
                    ? 'border-green-200 bg-gradient-to-br from-green-50 to-white'
                    : displayStatus === 'overdue'
                      ? 'border-red-200 bg-gradient-to-br from-red-50 to-white'
                      : displayStatus === 'in-progress'
                        ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white'
                        : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white'
                }`}
              >
                {/* Priority Indicator */}
                <div
                  className={`absolute left-0 top-0 h-full w-1 ${getPriorityColor(task.priority || 'medium')}`}
                />

                <div className="p-5">
                  <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        {task.status === 'completed' ? (
                          <CheckCircleIconSolid className="h-5 w-5 text-green-600" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                        )}
                        <h4
                          className={`text-lg font-bold ${
                            task.status === 'completed'
                              ? 'text-gray-500 line-through'
                              : 'text-gray-900'
                          }`}
                        >
                          {task.title}
                        </h4>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${getPriorityBadgeColor(
                            task.priority || 'medium'
                          )}`}
                        >
                          {task.priority === 'high' && (
                            <ExclamationTriangleIcon className="h-3 w-3" />
                          )}
                          {task.priority || 'medium'}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeColor(
                            displayStatus
                          )}`}
                        >
                          {displayStatus}
                        </span>
                      </div>

                      {task.description && (
                        <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-gray-600">
                          {task.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <UserIcon className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">
                            {task.assignedToName || task.assignedToEmail || 'Unassigned'}
                          </span>
                        </div>
                        {task.dueDate && (
                          <div
                            className={`flex items-center gap-1.5 ${
                              isOverdue ? 'font-semibold text-red-600' : ''
                            }`}
                          >
                            <CalendarIcon className="h-4 w-4" />
                            <span>
                              {new Date(task.dueDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                            {daysUntilDue !== null && !isOverdue && task.status !== 'completed' && (
                              <span className="text-gray-400">
                                ({daysUntilDue > 0 ? `${daysUntilDue} days left` : 'Due today'})
                              </span>
                            )}
                            {isOverdue && (
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        )}
                        {task.sessionId && (
                          <div className="flex items-center gap-1.5">
                            <TagIcon className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">From Session</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {task.status !== 'completed' && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => updateTaskStatus(task.id, 'completed')}
                          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-green-600 hover:to-emerald-700 hover:shadow-xl"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">Complete</span>
                        </motion.button>
                      )}
                      {task.status === 'pending' && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => updateTaskStatus(task.id, 'in-progress')}
                          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-600 hover:to-cyan-700 hover:shadow-xl"
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">Start</span>
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDeleteClick(task)}
                        className="rounded-xl bg-red-50 p-2.5 text-red-600 transition-all hover:bg-red-100 hover:shadow-lg"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Add Task Modal */}
      <AnimatePresence>
        {showAddTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={() => setShowAddTask(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 mb-6 flex items-center justify-between border-b bg-white pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
                    <PlusIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Create Follow-up Task</h3>
                    <p className="text-sm text-gray-500">Add a new action item from your session</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddTask(false)}
                  className="rounded-xl p-2 transition-colors hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Task Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Enter task title"
                    className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                    placeholder="Add task details..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Assign To <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newTask.assignedToEmail}
                      onChange={e => {
                        const selectedClient = clients.find(c => c.email === e.target.value);
                        setNewTask({
                          ...newTask,
                          assignedToEmail: e.target.value,
                          assignedToName: selectedClient
                            ? `${selectedClient.firstname || ''} ${selectedClient.lastname || ''}`.trim() ||
                              selectedClient.email
                            : '',
                        });
                      }}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                    >
                      <option value="">Select a client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.email}>
                          {client.firstname} {client.lastname} - {client.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={newTask.dueDate}
                      onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Priority
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Related Session (Optional)
                    </label>
                    <select
                      value={newTask.sessionId}
                      onChange={e => setNewTask({ ...newTask, sessionId: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                    >
                      <option value="">None</option>
                      {sessions.map(session => (
                        <option key={session.id} value={session.id}>
                          {session.clientName} -{' '}
                          {session.date ? new Date(session.date).toLocaleDateString() : 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-white pb-2 pt-4 sm:flex-row">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={addTask}
                    disabled={loading}
                    className="flex-1 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-orange-600 hover:to-red-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Task'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAddTask(false)}
                    className="flex-1 rounded-xl border-2 border-gray-300 bg-white px-6 py-3.5 text-sm font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        itemName={taskToDelete?.title}
      />
    </motion.div>
  );
};

export default FollowUpTasks;
